import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import StoreHeader   from "../../components/layout/StoreHeader";
import StoreFooter   from "../../components/layout/StoreFooter";
import VehicleSelector from "../../components/VehicleSelector";
import ProductCard   from "../../components/ProductCard";
import {
  ShieldCheck, Phone, ArrowRight, Star,
  ChevronLeft, ChevronRight, Pause, Play, Clock, X, Truck, Award, Car,
} from "lucide-react";

// ─── Hero slides ──────────────────────────────────────────────────────────
const SLIDES = [
  { badge:"Brisbane's Trusted Supplier",  title:"Quality Used &\nAftermarket Car Parts",       sub:"Ford · Toyota · Lexus · Honda · Mitsubishi · Nissan · Subaru · Isuzu · Kia · Range Rover", cta:"Shop All Parts",    href:"/shop" },
  { badge:"30–90 Day Warranty",           title:"Part Not Listed?\nWe'll Find It For You.",    sub:"Our team sources parts across Brisbane and Australia-wide. Request a free quote.",         cta:"Request a Quote",  href:"/quote" },
  { badge:"Same-Day Dispatch Before 2pm", title:"Ford 4×4 Specialists\nLocal Brisbane Dealer", sub:"Order before 2pm and your part ships same day — right across QLD and nationwide.",        cta:"Browse Ford Parts", href:"/shop?vehicle_make=Ford" },
];

// Exact-match images for BCP's actual top categories — real photos of real stock,
// supplied directly by BCP (not stock photos). Falls back to a neutral parts
// image only if a category outside this curated top-12 list is ever shown.
const CATEGORY_IMAGE_MAP = {
  RIGHT_DOOR_MIRROR:       "/part-images/right_door_mirror.webp",
  LEFT_DOOR_MIRROR:        "/part-images/left_door_mirror.webp",
  LEFT_TAILLIGHT:          "/part-images/left_taillight.webp",
  SUNVISOR:                "/part-images/sun_visor.webp",
  RIGHT_TAILLIGHT:         "/part-images/right_taillight.webp",
  RADIO_CD_DVD_SAT_TV:     "/part-images/radio.webp",
  RIGHT_HEADLAMP:          "/part-images/right_headlamp.webp",
  REAR_BUMPER:             "/part-images/rear_bumper.webp",
  BOOTLID_TAILGATE:        "/part-images/tailgate.webp",
  LEFT_HEADLAMP:           "/part-images/left_headlamp.webp",
  FRONT_SEAT:              "/part-images/front_seat.webp",
  FAN:                     "/part-images/fan.webp",
};
const CATEGORY_FALLBACK_IMG = "https://images.unsplash.com/photo-1441148345475-03a2e82f9719?w=400&q=80";

const formatCategory = (code) => {
  if (!code) return "";
  return code.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

const imageForCategory = (rawCode) => CATEGORY_IMAGE_MAP[rawCode] || CATEGORY_FALLBACK_IMG;


// Brand colours for known makes — falls back to BCP navy if make isn't in this list
// Logos removed (external Wikipedia URLs were unreliable) — clean wordmark always used instead
const MAKE_COLORS = {
  TOYOTA: "#EB0A1E", HONDA: "#E40521", FORD: "#003478", NISSAN: "#C3002F",
  SUBARU: "#003399", MITSUBISHI: "#E60012", HYUNDAI: "#002C5F", MAZDA: "#910A2D",
  KIA: "#05141F", ISUZU: "#CE1124", LEXUS: "#1A1A1A", "LAND ROVER": "#005A2B",
  "RANGE ROVER": "#005A2B", VOLKSWAGEN: "#001E50", BMW: "#0066B1", MERCEDES: "#999999",
  AUDI: "#BB0A30", HOLDEN: "#FFC72C", SUZUKI: "#E30613", JEEP: "#424B26",
  VOLVO: "#003057", PEUGEOT: "#0B1A3B", RENAULT: "#FFCC00", SKODA: "#4BA82E",
  CHRYSLER: "#000000", DODGE: "#BA0C2F", GREAT_WALL: "#BE1622", LDV: "#1E5BB8",
};
const colorForMake = (name) => MAKE_COLORS[(name || "").toUpperCase()] || "#0b1a3b";

// ─── Social proof notifications ───────────────────────────────────────────
const SOCIAL_PROOF = [
  { location:"Ipswich, QLD",        part:"Toyota Hilux 1KZ-TE Engine",        mins:3  },
  { location:"Toowoomba, QLD",      part:"Ford Ranger 6-Speed Gearbox",        mins:9  },
  { location:"Gold Coast, QLD",     part:"Mazda CX-5 Front Bumper Assembly",   mins:16 },
  { location:"Sunshine Coast, QLD", part:"Subaru Outback Strut Assembly",      mins:24 },
  { location:"Caboolture, QLD",     part:"200 Series Landcruiser Transfer",    mins:33 },
  { location:"Springfield, QLD",    part:"Nissan Navara Right Front Door",     mins:41 },
  { location:"Redcliffe, QLD",      part:"Honda CRV Alternator",               mins:47 },
  { location:"Logan Central, QLD",  part:"Mitsubishi Triton Rear Diff",        mins:55 },
  { location:"Beenleigh, QLD",      part:"Toyota Camry Auto Transmission",     mins:62 },
  { location:"Morayfield, QLD",     part:"Hyundai Tucson Engine Assembly",     mins:70 },
];

// ─── Reviews ──────────────────────────────────────────────────────────────
const REVIEWS = [
  { name:"Jason Mullins", date:"2 weeks ago",  stars:5, text:"Found an engine for my Hilux at half the price of a new one. Staff were super helpful and it was shipped next day. 100% would recommend BCP to anyone." },
  { name:"Sarah Chen",    date:"1 month ago",  stars:5, text:"Ordered a gearbox for my Subaru. Arrived well-packaged and works perfectly. Great communication throughout. Will be back next time I need parts." },
  { name:"Dave Kovacs",   date:"3 weeks ago",  stars:5, text:"Called about a specific bumper and they actually went out and found it for me same afternoon. These guys know their stuff. Pickup was easy too." },
  { name:"Mark Brennan",  date:"1 month ago",  stars:5, text:"Third time using BCP. Never let me down yet. Got a whole front end kit for my Ranger for an unbeatable price. Fast delivery to Ipswich." },
  { name:"Priya Sharma",  date:"2 months ago", stars:4, text:"Good price on the suspension parts I needed. Arrived in 2 days which was impressive. Only minor feedback is they could improve the website search filters." },
  { name:"Tom Nguyen",    date:"3 weeks ago",  stars:5, text:"Spoke to the team about a rare part for my '99 Land Cruiser. They found it within 48 hours and shipped it up to Townsville. Excellent service!" },
  { name:"Lisa Park",     date:"1 month ago",  stars:5, text:"As a mechanic I deal with wreckers all the time. BCP stands out for reliability and fair pricing. My go-to for Japanese and Korean parts." },
  { name:"Craig Wilson",  date:"6 weeks ago",  stars:5, text:"Bought a door assembly for my Ranger. Perfect fit, great price, delivery was quick. Couldn't ask for more from a Brisbane wrecker." },
];

// ─── Hooks ────────────────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function useCountUp(target, duration, trigger) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!trigger || typeof target !== "number") return;
    let startTime = null;
    let rafId;
    const animate = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.round((1 - Math.pow(1 - progress, 3)) * target));
      if (progress < 1) rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [trigger, target, duration]);
  return value;
}

// ─── Stars ────────────────────────────────────────────────────────────────
function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i < n ? "fill-[#f59e0b] text-[#f59e0b]" : "fill-zinc-200 text-zinc-200"}`} />
      ))}
    </div>
  );
}

// Make Card — clean brand-colour wordmark (no external logo dependency)
// Display-only label override — doesn't affect filtering/data, just the visible text
const MAKE_DISPLAY_LABEL = { Mercedes: "Mercedes-Benz" };

function MakeCard({ name, color }) {
  const displayName = MAKE_DISPLAY_LABEL[name] || name;
  return (
    <Link
      to={`/shop?vehicle_make=${encodeURIComponent(name)}`}
      className="group relative flex flex-col items-center justify-center gap-2.5 p-5 bg-white border border-zinc-200 rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-transparent"
      style={{ minHeight: "120px" }}
    >
      {/* Brand-colour bar slides up from bottom on hover */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] translate-y-full group-hover:translate-y-0 transition-transform duration-300"
        style={{ background: color }}
      />

      <Car className="h-7 w-7 group-hover:scale-110 transition-transform duration-300" style={{ color }} strokeWidth={1.75} />

      <span
        className="font-heading font-black text-[14px] uppercase text-center leading-tight tracking-wide"
        style={{ color }}
      >
        {displayName}
      </span>
    </Link>
  );
}

// ─── Social Proof Popup ───────────────────────────────────────────────────
function SocialProofPopup() {
  const [item, setItem]       = useState(null);
  const [visible, setVisible] = useState(false);
  const indexRef              = useRef(0);

  useEffect(() => {
    const show = () => {
      const current = SOCIAL_PROOF[indexRef.current % SOCIAL_PROOF.length];
      indexRef.current += 1;
      setItem(current);
      setVisible(true);
      setTimeout(() => setVisible(false), 6000);
    };
    const first    = setTimeout(show, 6000);
    const interval = setInterval(show, 22000);
    return () => { clearTimeout(first); clearInterval(interval); };
  }, []);

  if (!item) return null;

  return (
    <div className={`fixed bottom-6 left-6 z-50 transition-all duration-500 ease-out ${visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0 pointer-events-none"}`}>
      <div className="relative bg-white border border-zinc-200 shadow-2xl max-w-[290px] overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#e31e24]" />
        <div className="pl-4 pr-3 py-3.5 flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 bg-[#e31e24]/10 border border-[#e31e24]/20 flex items-center justify-center mt-0.5">
            <Truck className="h-4 w-4 text-[#e31e24]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shrink-0" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-green-600 font-bold">Just Purchased</span>
            </div>
            <p className="text-[13px] font-bold text-[#0b1a3b] leading-snug line-clamp-2 mb-1">{item.part}</p>
            <div className="flex items-center gap-1 text-[11px] text-zinc-400">
              <Clock className="h-3 w-3" />
              <span>{item.mins} min ago · {item.location}</span>
            </div>
          </div>
          <button onClick={() => setVisible(false)} className="shrink-0 text-zinc-300 hover:text-zinc-500 transition-colors p-0.5">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────
export default function Home() {
  const [featured,    setFeatured]    = useState([]);
  const [arrivals,    setArrivals]    = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [topMakes,      setTopMakes]      = useState([]);
  const [slide,       setSlide]       = useState(0);
  const [videoPaused, setVideoPaused] = useState(false);
  const [googleReviews, setGoogleReviews] = useState({ reviews: REVIEWS, rating: 4.8, total: null });
  const heroVideoRef = useRef(null);

  const [catRef,     catVisible]     = useReveal();
  const [howRef,     howVisible]     = useReveal();
  const [dismantRef, dismantVisible] = useReveal();
  const [makesRef,   makesVisible]   = useReveal();
  const [whyRef,     whyVisible]     = useReveal();

  const years = useCountUp(15, 1800, whyVisible);
  const days  = useCountUp(90, 2200, whyVisible);

  useEffect(() => {
    api.get("/products?page_size=8&sort=newest&in_stock=true").then((r) => {
      const items = r.data.items || [];
      setFeatured(items.slice(0, 8));
      setArrivals([...items, ...items]);
    }).catch(() => {});
  }, []);

  // Real Google reviews — fetched from Places API, falls back to static reviews
  useEffect(() => {
    api.get("/google-reviews").then((r) => {
      if (r.data.reviews?.length > 0) {
        setGoogleReviews({
          reviews: r.data.reviews.map(rv => ({
            name:  rv.name,
            stars: rv.rating,
            text:  rv.text,
            date:  rv.time,
            photo: rv.photo,
          })),
          rating: r.data.rating,
          total:  r.data.total,
        });
      }
    }).catch(() => {}); // silently fall back to static reviews
  }, []);

  // Real categories — top 12 by part count, from actual Pinnacle data
  useEffect(() => {
    api.get("/products/facets").then((r) => {
      const cats = (r.data.categories || [])
        .filter((c) => c && c.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 11)
        .map((c) => ({
          raw: c.name,
          label: formatCategory(c.name),
          href: `/shop?category=${encodeURIComponent(c.name)}`,
          img: imageForCategory(c.name),
        }));
      setTopCategories(cats);
    }).catch(() => {});
  }, []);

  // Real makes — top 12 by actual stock count (not alphabetical)
  useEffect(() => {
    api.get("/products/facets").then((r) => {
      const list = (r.data.vehicle_makes_by_count || [])
        .slice(0, 12)
        .map((m) => ({ name: m.name, color: colorForMake(m.name) }));
      setTopMakes(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const prev = () => setSlide((s) => (s - 1 + SLIDES.length) % SLIDES.length);
  const next = () => setSlide((s) => (s + 1) % SLIDES.length);

  const toggleVideo = () => {
    if (!heroVideoRef.current) return;
    if (heroVideoRef.current.paused) { heroVideoRef.current.play(); setVideoPaused(false); }
    else { heroVideoRef.current.pause(); setVideoPaused(true); }
  };

  return (
    <div className="bg-white min-h-screen">
      <StoreHeader />
      <SocialProofPopup />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative h-[500px] md:h-[600px] overflow-hidden">
        <video ref={heroVideoRef} autoPlay muted loop playsInline
          poster="/videos/hero-poster.jpg"
          className="absolute inset-0 w-full h-full object-cover">
          <source src="/videos/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1a3b]/90 via-[#0b1a3b]/55 to-[#0b1a3b]/20" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0b1a3b]/60 to-transparent" />
        <div className="relative h-full max-w-7xl mx-auto px-4 md:px-8 flex flex-col justify-center">
          {SLIDES.map((s, i) => (
            <div key={i} className={`absolute max-w-xl transition-all duration-700 ${i === slide ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
              <span className="inline-block bg-[#e31e24] text-white text-[11px] font-mono font-bold uppercase tracking-[0.3em] px-3 py-1.5 mb-5">{s.badge}</span>
              <h1 className="font-heading text-4xl md:text-[52px] font-black uppercase leading-[1.08] text-white mb-4" style={{ whiteSpace:"pre-line" }}>{s.title}</h1>
              <p className="text-blue-100 text-base md:text-[17px] mb-7 leading-relaxed font-light">{s.sub}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={s.href} className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#e31e24] hover:bg-[#b71820] text-white font-heading font-bold uppercase tracking-wide text-[15px] transition-colors">
                  {s.cta} <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="tel:0738751024" className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-white/60 hover:border-white text-white font-heading font-semibold uppercase tracking-wide text-[15px] transition-colors">
                  <Phone className="h-4 w-4" /> Call 07 3875 1024
                </a>
              </div>
            </div>
          ))}
        </div>
        <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors z-10"><ChevronLeft className="h-5 w-5" /></button>
        <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors z-10"><ChevronRight className="h-5 w-5" /></button>
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
          <button onClick={toggleVideo} className="w-7 h-7 bg-white/20 hover:bg-white/35 text-white flex items-center justify-center rounded-full transition-colors">
            {videoPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
          </button>
          <div className="flex gap-2">
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-[#e31e24]" : "w-3 bg-white/40"}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ── VEHICLE FINDER ───────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-10 relative z-10 mb-16">
        <VehicleSelector variant="hero" />
      </div>

      {/* ── TRUST BADGES ─────────────────────────────────────────────── */}
      <section className="bg-[#0b1a3b] py-8 mb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon:ShieldCheck, label:"90-Day Warranty",   sub:"On most used parts"    },
            { icon:Clock,       label:"Same-Day Dispatch", sub:"Order before 2pm"       },
            { icon:Award,       label:"Brisbane Local",    sub:"Pick up available"      },
            { icon:Phone,       label:"Expert Support",    sub:"Mon–Sat helpline"       },
          ].map(({ icon:Icon, label, sub }) => (
            <div key={label} className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#e31e24]/15 border border-[#e31e24]/30 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-[#e31e24]" />
              </div>
              <div>
                <div className="font-heading font-bold text-white text-[15px] tracking-wide">{label}</div>
                <div className="text-blue-300 text-[12px] mt-0.5">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SHOP BY PART TYPE ────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Browse by type</div>
            <h2 className="font-heading text-[34px] font-bold text-[#0b1a3b] leading-tight">Shop by Part Type</h2>
          </div>
          <Link to="/shop" className="hidden md:flex items-center gap-2 text-[13px] font-semibold text-[#1e5bb8] hover:text-[#0b3ea0] tracking-wide">
            All parts <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div ref={catRef} className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 reveal-up ${catVisible ? "visible" : ""}`}>
          {topCategories.map(({ raw, label, href, img }) => (
            <Link key={raw} to={href}
              className="group relative overflow-hidden rounded-sm aspect-square border border-zinc-200 hover:border-[#1e5bb8] hover:shadow-xl transition-all duration-300">
              <img src={img} alt={label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b1a3b]/88 via-[#0b1a3b]/20 to-transparent group-hover:from-[#1e5bb8]/88 transition-colors duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="font-heading font-semibold text-white text-[12px] leading-tight">{label}</div>
              </div>
            </Link>
          ))}
          <Link to="/shop"
            className="group relative overflow-hidden rounded-sm aspect-square border border-zinc-200 hover:border-[#e31e24] hover:shadow-xl transition-all duration-300 bg-[#0b1a3b] flex items-center justify-center">
            <div className="text-center px-2">
              <div className="font-heading font-bold text-white text-[13px] leading-tight mb-1">All Parts</div>
              <ArrowRight className="h-4 w-4 text-white mx-auto group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
      <section ref={howRef} className={`bg-zinc-50 border-y border-zinc-200 py-16 mb-16 reveal-up ${howVisible ? "visible" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Simple process</div>
            <h2 className="font-heading text-[34px] font-bold text-[#0b1a3b]">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { n:"01", title:"Search Your Part",       body:"Enter your Make, Model, Year or search by part name, SKU or OEM number to find the exact part from our inventory." },
              { n:"02", title:"Order or Call",           body:"Checkout securely online, or call our Brisbane team on 07 3875 1024 for expert help finding your part." },
              { n:"03", title:"Fast Delivery or Pickup", body:"Order before 1pm for same-day dispatch. Australia-wide delivery available, or collect from our Brisbane yard." },
            ].map(({ n, title, body }) => (
              <div key={n} className="flex gap-5">
                <div className="font-heading text-5xl font-black text-[#e31e24]/20 leading-none w-14 shrink-0 pt-1">{n}</div>
                <div>
                  <h3 className="font-heading font-bold text-[17px] text-[#0b1a3b] mb-2">{title}</h3>
                  <p className="text-[15px] text-zinc-500 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED PRODUCTS ────────────────────────────────────────── */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 mb-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Hand picked</div>
              <h2 className="font-heading text-[34px] font-bold text-[#0b1a3b] leading-tight">Featured Parts</h2>
            </div>
            <Link to="/shop?sort=newest" className="hidden md:flex items-center gap-2 text-[13px] font-semibold text-[#1e5bb8] hover:text-[#0b3ea0] tracking-wide">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* ── NEW ARRIVALS MARQUEE ──────────────────────────────────────── */}
      {arrivals.length > 0 && (
        <section className="bg-[#0b1a3b] py-12 mb-16 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 md:px-8 mb-6 flex items-end justify-between">
            <div>
              <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Just in</div>
              <h2 className="font-heading text-[34px] font-bold text-white leading-tight">New Arrivals</h2>
            </div>
            <Link to="/shop?sort=newest" className="flex items-center gap-2 text-[13px] font-semibold text-blue-300 hover:text-white tracking-wide">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="marquee-wrapper">
            <div className="marquee-track gap-4 pl-4">
              {arrivals.concat(arrivals).map((p, i) => (
                <div key={`${p.id}-${i}`} className="w-56 shrink-0"><ProductCard product={p} /></div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CURRENTLY DISMANTLING — Yard Video ───────────────────────── */}
      <section ref={dismantRef} className={`bg-white border-y border-zinc-200 py-16 mb-16 reveal-up ${dismantVisible ? "visible" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-3">Fresh off the yard</div>
              <h2 className="font-heading text-[34px] font-bold text-[#0b1a3b] mb-4 leading-tight">Currently<br />Dismantling</h2>
              <p className="text-[16px] text-zinc-500 leading-relaxed mb-6">
                We take in new vehicles every week. Parts are pulled fresh — engines, gearboxes, body panels, interior trim and more. If you need a specific part from a vehicle we're currently working on, contact us and we'll pull it for you.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-7">
                {["Engines & Gearboxes","Body Panels","Doors & Glass","Interior Trim","Suspension Parts","Brake Systems","Electrical Parts","Wheels & Tyres"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-[14px] text-zinc-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#e31e24] shrink-0" /> {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/quote" className="inline-flex items-center gap-2 px-6 py-3 bg-[#e31e24] hover:bg-[#b71820] text-white font-heading font-semibold tracking-wide text-[14px] transition-colors">
                  Request a Part <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="tel:0738751024" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#0b1a3b] hover:bg-[#0b1a3b] hover:text-white text-[#0b1a3b] font-heading font-semibold tracking-wide text-[14px] transition-colors">
                  <Phone className="h-4 w-4" /> Call Now
                </a>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-sm shadow-2xl">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#e31e24] z-10" />
              <video autoPlay muted loop playsInline poster="/videos/yard-poster.jpg" className="w-full aspect-video object-cover">
                <source src="/videos/yard-video.mp4" type="video/mp4" />
              </video>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0b1a3b]/80 to-transparent p-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#e31e24] animate-pulse" />
                  <span className="font-mono text-[11px] uppercase tracking-widest text-white font-bold">Live from the yard</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAKES WE COVER ───────────────────────────────────────────── */}
      <section ref={makesRef} className={`bg-zinc-50 py-16 mb-16 reveal-up ${makesVisible ? "visible" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Our specialty</div>
            <h2 className="font-heading text-[34px] font-bold text-[#0b1a3b]">Makes We Cover</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {topMakes.map(({ name, color }) => (
              <MakeCard key={name} name={name} color={color} />
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY BCP BRISBANE ─────────────────────────────────────────── */}
      <section ref={whyRef} className={`bg-[#0b1a3b] py-16 mb-16 reveal-up ${whyVisible ? "visible" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Local expertise</div>
            <h2 className="font-heading text-[34px] font-bold text-white">Why BCP Brisbane?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-white/10">
            <div className="text-center px-6 pb-8 md:pb-0">
              <div className="font-heading text-[68px] font-black text-[#e31e24] leading-none mb-2 tabular-nums">{whyVisible ? `${years}+` : "0+"}</div>
              <div className="font-heading font-semibold text-[17px] text-white mb-3 tracking-wide">Years Experience</div>
              <p className="text-blue-300 text-[14px] leading-relaxed">Over a decade serving Brisbane and QLD drivers with quality used parts at fair prices.</p>
            </div>
            <div className="text-center px-6 py-8 md:py-0">
              <div className="font-heading text-[68px] font-black text-[#e31e24] leading-none mb-2 tabular-nums">{whyVisible ? days : "0"}</div>
              <div className="font-heading font-semibold text-[17px] text-white mb-3 tracking-wide">Day Warranty</div>
              <p className="text-blue-300 text-[14px] leading-relaxed">Most of our used parts come with a 90-day warranty. If something's wrong, we make it right — no runaround.</p>
            </div>
            <div className="text-center px-6 pt-8 md:pt-0">
              <div className="font-heading text-[68px] font-black text-[#e31e24] leading-none mb-2">AUS</div>
              <div className="font-heading font-semibold text-[17px] text-white mb-3 tracking-wide">Wide Delivery</div>
              <p className="text-blue-300 text-[14px] leading-relaxed">Order before 1pm for same-day dispatch. Australia-wide delivery to your door or workshop.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── GOOGLE REVIEWS ───────────────────────────────────────────── */}
      <section className="py-16 mb-16 overflow-hidden border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 mb-8">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Customer reviews</div>
              <h2 className="font-heading text-[34px] font-bold text-[#0b1a3b]">What Customers Say</h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {Array(5).fill(0).map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-[#f59e0b] text-[#f59e0b]"/>
                ))}
              </div>
              <span className="font-heading font-bold text-[22px] text-[#0b1a3b]">
                {googleReviews.rating ? googleReviews.rating.toFixed(1) : "4.8"}
              </span>
              <span className="text-zinc-400 text-[14px]">on Google</span>
              {googleReviews.total && (
                <span className="text-zinc-400 text-[13px]">· {googleReviews.total.toLocaleString()} reviews</span>
              )}
            </div>
          </div>
        </div>
        <div className="marquee-wrapper">
          <div className="review-track gap-4 pl-4">
            {[...googleReviews.reviews, ...googleReviews.reviews].map((r, i) => (
              <div key={i} className="w-72 shrink-0 bg-white border border-zinc-200 rounded-sm p-5 hover:border-[#1e5bb8] hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <Stars n={r.stars}/>
                  <span className="text-[11px] text-zinc-400 font-mono">{r.date}</span>
                </div>
                <p className="text-[14px] text-zinc-600 leading-relaxed mb-3 line-clamp-4">{r.text}</p>
                <div className="flex items-center gap-2">
                  {r.photo ? (
                    <img src={r.photo} alt={r.name} className="w-7 h-7 rounded-full object-cover"/>
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#1e5bb8] text-white flex items-center justify-center text-[11px] font-bold">
                      {r.name?.[0] || "G"}
                    </div>
                  )}
                  <span className="font-medium text-[13px] text-[#0b1a3b]">{r.name}</span>
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="h-3.5 w-3.5 ml-auto opacity-60"/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <section className="bg-zinc-50 border-t border-zinc-200 py-16 mb-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-3">Can't find your part?</div>
          <h2 className="font-heading text-[34px] font-bold text-[#0b1a3b] mb-4 leading-tight">We'll Source It For You</h2>
          <p className="text-zinc-500 text-[16px] max-w-lg mx-auto mb-8 leading-relaxed">
            Our team has access to a nationwide network of wreckers and importers. If it exists in Australia, we'll find it — and get it to you fast.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/quote" className="inline-flex items-center gap-2 px-8 py-4 bg-[#e31e24] hover:bg-[#b71820] text-white font-heading font-semibold tracking-wide text-[15px] transition-colors">
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="tel:0738751024" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#0b1a3b] hover:bg-[#0b1a3b] hover:text-white text-[#0b1a3b] font-heading font-semibold tracking-wide text-[15px] transition-colors">
              <Phone className="h-4 w-4" /> 07 3875 1024
            </a>
          </div>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
}